import React from "react";
import {
  Target,
  Heart,
  Mountain,
  Shield,
  Users,
  Bike,
  Footprints,
  Calendar,
  Clock,
  MapPin,
  Facebook,
  Phone,
  Mail,
  CheckCircle,
  TreePine,
  Handshake,
  GraduationCap,
  Utensils,
} from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/associazione-01-gruppo-montagna.jpg";

const GALLERY_IMAGES = [
  { src: "/images/associazione-01-gruppo-montagna.jpg", alt: "Escursione in montagna" },
  { src: "/images/associazione-02-selfie-neve.jpg", alt: "Escursione invernale" },
  { src: "/images/associazione-03-ciclisti-castello.jpg", alt: "Ciclisti davanti al castello" },
  { src: "/images/associazione-04-gruppo-sassi.jpg", alt: "Pausa durante escursione" },
  { src: "/images/associazione-05-selfie-due-neve.jpg", alt: "Amici sulla neve" },
  { src: "/images/associazione-06-ciclisti-mare.jpg", alt: "Pedalo Ma Non Troppo" },
];

// ============================================
// DATI
// ============================================

const obiettivi = [
  {
    icon: Mountain,
    title: "Riscoperta dei 99 Castelli",
    description:
      "Continuare nella ricerca e riscoperta dei 99 Castelli che contribuirono a fondare la città di L'Aquila.",
  },
  {
    icon: Bike,
    title: "Uscite in Bicicletta e a Piedi",
    description:
      "Organizzare uscite per riscoprire Castelli ed altri siti di interesse storico/culturale in modo lento, sostenibile ed informato.",
  },
  {
    icon: TreePine,
    title: "Tutela Ambientale",
    description:
      "Organizzare attività di tutela dei Castelli e dell'ambiente, svolgendo opere di pulizia e disberbamento in accordo con proprietari e gestori.",
  },
  {
    icon: Handshake,
    title: "Collaborazioni",
    description:
      "Organizzare eventi e manifestazioni in collaborazione con altre Associazioni o Enti pubblici per valorizzare il territorio e le tradizioni locali.",
  },
  {
    icon: GraduationCap,
    title: "Divulgazione",
    description:
      "Organizzare momenti di confronto e condivisione sulla storia dei Castelli, coinvolgendo Scuole, Enti ed altre Associazioni.",
  },
  {
    icon: Utensils,
    title: "Convivialità",
    description: "Godersi una piacevole giornata in compagnia, cercando sempre di finire... a tavola!",
  },
];

const valori = [
  {
    icon: Users,
    title: "Inclusività",
    description: "L'Associazione è aperta a tutti, indipendentemente dall'età e dal livello di preparazione fisica.",
  },
  {
    icon: Clock,
    title: "Nessuno Viene Lasciato Indietro",
    description: "Si andrà sempre al ritmo dell'ultimo. Le nostre attività non hanno carattere competitivo.",
  },
  {
    icon: Heart,
    title: "Passione",
    description: "Volenterosi di scoprire nuovi posti e la loro storia, senza fretta ma con entusiasmo.",
  },
  {
    icon: Shield,
    title: "Sicurezza",
    description:
      "Prima di ogni attività vengono comunicate le caratteristiche del percorso per permettere a ognuno di valutare.",
  },
];

const team = [
  { name: "Alessandro Serreli", role: "Fondatore e Presidente", description: "Guida Cicloturistica CSI" },
  { name: "Alessia", role: "Vice Presidente", description: "Supporto organizzativo" },
  { name: "Roberto", role: "Consigliere", description: "Compagno di mille avventure" },
];

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

const Associazione: React.FC = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        backgroundImage={HERO_IMAGE}
        title="L'Associazione"
        subtitle="Scopri chi siamo e la nostra missione per valorizzare i 99 Castelli"
        height="medium"
        overlay
        centered
      />

      {/* Chi Siamo - Alessandro Serreli */}
      <Section background="cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeader title="Chi Siamo" subtitle="La storia di una passione" align="left" />
              <div className="space-y-5 text-text-light font-body leading-relaxed">
                <p>
                  Mi presento: mi chiamo <strong className="text-foreground">Alessandro Serreli</strong>, classe 1982,
                  papà di due splendide bimbe e informatico di mestiere.
                </p>
                <p>
                  Fin da quando ero bambino sono stato affascinato dalla storia del nostro territorio e ho sempre
                  cercato le tracce di questo passato nei libri, nei racconti delle persone e nei luoghi.
                </p>
                <p>
                  La mia più grande passione è la <strong className="text-foreground">bicicletta</strong> e spesso mi
                  sono ritrovato in sella per cercare queste tracce, scoprendo posti poco conosciuti che nascondono
                  storie che danno tutt'ora nutrimento alla mia curiosità!
                </p>
                <p>
                  Nel <strong className="text-foreground">Maggio 2022</strong>, quasi per uno sfizio, ho seguito un
                  corso organizzato dal CSI - Comitato provinciale di L'Aquila per diventare
                  <strong className="text-foreground"> guida Cicloturistica</strong> e da lì è nata l'idea di andare a
                  riscoprire i Castelli che contribuirono a fondare la città di L'Aquila.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="/images/associazione-03-ciclisti-castello.jpg"
                alt="Ciclisti davanti al castello"
                className="rounded-xl shadow-elegant aspect-[3/4] object-cover"
                loading="lazy"
              />
              <img
                src="/images/associazione-01-gruppo-montagna.jpg"
                alt="Escursione in montagna"
                className="rounded-xl shadow-elegant aspect-[3/4] object-cover mt-8"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* La Nascita dell'Associazione */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader title="La Nascita di 99 Castelli" subtitle="Da una pagina Facebook a un'Associazione" />
          <div className="space-y-6 text-text-light font-body leading-relaxed">
            <p className="text-lg">
              A <strong className="text-foreground">Settembre 2022</strong> ho creato un logo ed ho aperto una pagina su
              Facebook dove ho iniziato a condividere le mie visite a questi Castelli. Pian piano ho cercato di reperire
              sempre più informazioni grazie anche alla collaborazione di persone gentili e preparate che ho avuto la
              fortuna di incontrare.
            </p>
            <p className="text-lg">
              A <strong className="text-foreground">Ottobre 2022</strong> ho voluto provare ad organizzare per la prima
              volta delle visite in bicicletta a dei Castelli, coinvolgendo gli amici di sempre ma anche persone che non
              conoscevo, e devo dire che abbiamo passato delle belle mattinate concluse poi a tavola!
            </p>
            <p className="text-lg">
              Da lì è poi nata l'idea di creare un'<strong className="text-foreground">Associazione</strong>, nella
              quale ho coinvolto persone importanti per me... prima di tutto mia moglie
              <strong className="text-foreground"> Alessia</strong>, che mi supporta e mi sopporta in questo progetto, e
              il mio migliore amico <strong className="text-foreground">Roberto</strong>, compagno di mille avventure
              che non sa mai dirmi di no, così come i nostri genitori!
            </p>
          </div>
        </div>
      </Section>

      {/* Pedalo Ma Non Troppo */}
      <Section background="cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="/images/associazione-06-ciclisti-mare.jpg"
                alt="Pedalo Ma Non Troppo"
                className="rounded-xl shadow-elegant-lg w-full"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <SectionHeader title="Pedalo Ma Non Troppo" subtitle="Il nostro gruppetto di amici" align="left" />
              <div className="space-y-5 text-text-light font-body leading-relaxed">
                <p>
                  E poi ci sono gli amici di sempre:{" "}
                  <strong className="text-foreground">Alessandro, Matteo, Diego, Pasquale e Francesco</strong>, con cui
                  ho condiviso avventure di ogni genere e tipo e con cui abbiamo formato un bel gruppetto chiamato
                  <strong className="text-foreground"> "Pedalo Ma Non Troppo"</strong>.
                </p>
                <p>Pronti a seguirmi in questa nuova esperienza: spero avrete modo di conoscerci in futuro!</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Obiettivi */}
      <Section background="white">
        <SectionHeader title="Obiettivi di ASD 99 Castelli" subtitle="Le finalità dell'Associazione" />
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-text-light text-lg mb-10 max-w-3xl mx-auto">
            Per perseguire il nostro obiettivo di accompagnarvi alla riscoperta del territorio aquilano, abbiamo fondato
            l'
            <strong className="text-foreground">
              Associazione Sportiva Dilettantistica senza scopo di lucro ASD 99 Castelli
            </strong>
            .
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {obiettivi.map((obiettivo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-background rounded-xl p-6 shadow-elegant hover:shadow-elegant-lg transition-all"
              >
                <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
                  <obiettivo.icon size={28} className="text-secondary" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">{obiettivo.title}</h3>
                <p className="text-text-light font-body text-sm">{obiettivo.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Le Nostre Attività */}
      <Section background="primary">
        <div className="max-w-4xl mx-auto text-center">
          <SectionHeader title="Le Nostre Attività" subtitle="Cosa organizziamo" light />
          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <Bike size={48} className="text-secondary mx-auto mb-4" />
              <h3 className="text-2xl font-heading font-bold text-white mb-2">Almeno 3 uscite in bicicletta</h3>
              <p className="text-white/80">ogni anno</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <Footprints size={48} className="text-secondary mx-auto mb-4" />
              <h3 className="text-2xl font-heading font-bold text-white mb-2">Almeno 3 uscite a piedi</h3>
              <p className="text-white/80">ogni anno</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-white/90">
            <div className="flex items-center gap-2">
              <Calendar size={20} className="text-secondary" />
              <span>Prevalentemente Sabato e Domenica mattina</span>
            </div>
          </div>
        </div>
      </Section>

      {/* I Nostri Valori */}
      <Section background="cream">
        <SectionHeader title="I Nostri Valori" subtitle="Come viviamo le nostre avventure" />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {valori.map((valore, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-elegant text-center"
            >
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <valore.icon size={28} className="text-primary" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">{valore.title}</h3>
              <p className="text-text-light font-body text-sm">{valore.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Highlight Box */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-secondary/10 border-l-4 border-secondary rounded-r-xl p-6">
            <p className="text-lg text-foreground font-medium italic">
              "L'Associazione è aperta a tutti coloro a cui piace pedalare o camminare, volenterosi di scoprire nuovi
              posti e la loro storia, indipendentemente dall'età, senza fretta ma solo con l'intento di godersi una
              piacevole giornata in compagnia che cercheremo sempre di finire a tavola!!!"
            </p>
          </div>
        </div>
      </Section>

      {/* Galleria */}
      <Section background="white">
        <SectionHeader title="Le Nostre Avventure" subtitle="Momenti catturati durante le nostre escursioni" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {GALLERY_IMAGES.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative overflow-hidden rounded-xl shadow-elegant group aspect-square"
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <p className="absolute bottom-4 left-4 text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                {image.alt}
              </p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Affiliazione CSI */}
      <Section background="cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeader
                title="Affiliazione CSI"
                subtitle="Centro Sportivo Italiano - Comitato di L'Aquila"
                align="left"
              />
              <div className="space-y-4 text-text-light font-body leading-relaxed">
                <p>
                  <strong className="text-foreground">ASD 99 Castelli</strong> è ufficialmente affiliata al
                  <strong className="text-foreground"> CSI - Centro Sportivo Italiano</strong>, Comitato di L'Aquila.
                </p>
                <p>Questa affiliazione garantisce ai nostri soci:</p>
                <ul className="space-y-3 ml-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-secondary mt-0.5 flex-shrink-0" />
                    <span>Copertura assicurativa infortuni per tutte le attività</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-secondary mt-0.5 flex-shrink-0" />
                    <span>Guide cicloturistiche certificate</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-secondary mt-0.5 flex-shrink-0" />
                    <span>Partecipazione a eventi e manifestazioni regionali e nazionali</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-secondary mt-0.5 flex-shrink-0" />
                    <span>Rete di collaborazione con altre associazioni affiliate</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="bg-white p-8 rounded-2xl shadow-elegant text-center">
                <div className="flex justify-center gap-6 mb-6">
                  <img src="/images/logo-99castelli.png" alt="ASD 99 Castelli" className="h-20 object-contain" />
                  <img src="/images/logo-icona.png" alt="Icona 99 Castelli" className="h-20 object-contain" />
                </div>
                <div className="border-t pt-6 space-y-2">
                  <p className="text-sm text-text-light">
                    Cod. Fiscale: <strong className="text-foreground">93116590667</strong>
                  </p>
                  <p className="text-sm text-text-light">
                    Cod. ASD: <strong className="text-foreground">06700181</strong>
                  </p>
                  <p className="text-sm text-text-light">
                    IBAN: <strong className="text-foreground text-xs">IT74T0832740700000000008756</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section background="primary">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            Vuoi Far Parte della Nostra Storia?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Unisciti a noi e scopri i castelli dell'Abruzzo insieme a una comunità di appassionati. Ogni tuo mi piace,
            ogni tuo commento e soprattutto la tua partecipazione sarà per noi uno stimolo a migliorare sempre di più!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button href="/tesseramento" variant="secondary" size="lg">
              Diventa Socio 2026
            </Button>
            <Button
              href="https://www.facebook.com/99castelli"
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary"
              icon={<Facebook size={20} />}
            >
              Seguici su Facebook
            </Button>
          </div>

          {/* Contact Info */}
          <div className="flex flex-wrap justify-center gap-6 text-white/90">
            <a href="tel:+393402894567" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone size={18} />
              340 2894567
            </a>
            <a
              href="mailto:99castelli@gmail.com"
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <Mail size={18} />
              99castelli@gmail.com
            </a>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default Associazione;
